<template>
  <div class="app-event-creation">
    <BannerTitle title="Event Reporting" />

    <section class="py-12">
      <v-container :fluid="!this.lgBreakpoint">
        <v-row>
          <v-col cols="12" md="4">
            <div class="date-time-picker-wrap">
              <v-menu
                v-model="startDateMenu"
                :close-on-content-click="false"
                transition="scale-transition"
                offset-y
                min-width="auto"
              >
                <template v-slot:activator="{ on, attrs }">
                  <v-text-field
                    ref="startDateField"
                    v-model="startDate"
                    label="Event Start Date"
                    append-icon="$Calendar"
                    readonly
                    v-bind="attrs"
                    hide-details="auto"
                    v-on="on"
                    :rules="[v => !!v || 'Event start date is required.']"
                    :disabled="loading"
                  />
                </template>
                <v-date-picker
                  v-model="startDate"
                  @input="startDateMenu = false"
                  no-title
                />
              </v-menu>
            </div>
          </v-col>

          <v-col cols="12" md="4">
            <div class="date-time-picker-wrap">
              <v-menu
                v-model="endDateMenu"
                :close-on-content-click="false"
                min-width="auto"
                offset-y
                transition="scale-transition"
              >
                <template v-slot:activator="{ on, attrs }">
                  <v-text-field
                    ref="endDateField"
                    v-model="endDate"
                    label="Event End Date"
                    append-icon="$Calendar"
                    readonly
                    v-bind="attrs"
                    hide-details="auto"
                    :rules="[
                      v => !!v || 'Event end date is required.',
                      v => checkIfEndDateIsValid(v)
                    ]"
                    :disabled="loading"
                    v-on="on"
                  />
                </template>
                <v-date-picker
                  v-model="endDate"
                  @input="endDateMenu = false"
                  :min="minEndDateStart"
                  no-title
                />
              </v-menu>
            </div>
          </v-col>

          <v-col>
            <v-btn
              class="px-10 mr-2 btn__primary"
              tile
              :disabled="loading"
              @click="downloadReportPDF"
            >
              Download PDF
            </v-btn>

            <v-btn
              class="px-10 btn__primary"
              tile
              :disabled="loading"
              @click="downloadReportCSV"
            >
              Download CSV
            </v-btn>
          </v-col>
        </v-row>

        <v-row class="mb-4">
          <v-col cols="4">
            <ReportingCard :quantity="CURRENT_EVENT_OBJECT.qrCodeScans || 0" title="QR Scans" />
          </v-col>
          <v-col cols="4">
            <ReportingCard :quantity="CURRENT_EVENT_OBJECT.engagements || 0" title="Leads" />
          </v-col>
        </v-row>

        <v-row>
          <v-col cols="4">
            <ReportingColumn
              :data="CURRENT_EVENT_OBJECT.leads_zip || []"
              :loading="loading"
              column-key="zip_code"
              title="Total Leads by Zip Code"
            />
          </v-col>
          <v-col cols="4">
            <ReportingColumn
              :data="CURRENT_EVENT_OBJECT.leads_wireless || []"
              :loading="loading"
              title="Total Leads by Wireless Provider"
            />
          </v-col>
          <v-col cols="4">
            <ReportingColumn
              :data="CURRENT_EVENT_OBJECT.leads_provider || []"
              :loading="loading"
              title="Total Leads by Home Provider"
            />
          </v-col>
        </v-row>
      </v-container>
    </section>

    <PDFGenerator ref="generator" />
  </div>
</template>

<script>
import BannerTitle from '@/components/BannerTitle.vue';
import ReportingCard from '@/components/EventReporting/ReportingCard.vue';
import ReportingColumn from '@/components/EventReporting/ReportingColumn.vue';
import PDFGenerator from '@/components/PDFGenerator.vue';
import { mapActions, mapGetters, mapState } from 'vuex';
import ExportTools from '@/utility/ExportTools.js';

export default {
  components: { BannerTitle, ReportingColumn, ReportingCard, PDFGenerator },

  computed: {
    ...mapGetters({
      IS_ADMIN: `account/IS_ADMIN`
    }),
    ...mapState('EVENT_MANAGER_STORE', [
      'CURRENT_EVENT_OBJECT',
      'LOADING',
      'BITLY_LINK',
      'API_CALL_IN_PROGRESS'
    ]),

    endDate: {
      get() {
        return this.CURRENT_EVENT_OBJECT?.selectedEndDate || this.CURRENT_EVENT_OBJECT?.endDate || null;
      },
      set(value) {
        this.$store.commit('EVENT_MANAGER_STORE/UPDATE_EVENT_END_DATE_TIME', {
          key: 'selectedEndDate',
          value: value
        });

        if (!this.loading) {
          void this.loadData();
        }
      }
    },

    lgBreakpoint: function() {
      return this.$vuetify.breakpoint.lgAndUp;
    },

    minStartDate() {
      const dt = new Date();

      if (!this.IS_ADMIN) {
        dt.setDate(dt.getDate() + 1);
      }

      return dt.toISOString().substring(0, 10);
    },

    minEndDateStart() {
      if (!this.startDate) {
        return this.minStartDate;
      }

      const dt = new Date(this.startDate);
      dt.setDate(dt.getDate());

      return dt.toISOString().substring(0, 10);
    },

    startDate: {
      get() {
        return this.CURRENT_EVENT_OBJECT?.selectedStartDate || this.CURRENT_EVENT_OBJECT.startDate || null;
      },
      set(value) {
        //Reset endTime;
        if (
          (value && this.endDate && value > this.endDate) ||
          (value === this.endDate &&
            this.startTime &&
            this.endTime &&
            this.startTime > this.endTime)
        ) {
          this.$store.commit('EVENT_MANAGER_STORE/UPDATE_EVENT_END_DATE_TIME', {
            key: 'endDate',
            value: null
          });
        }
        // reset startTime
        if (!this.IS_ADMIN && value != null && this.startTime != null) {
          // get difference in hours
          let date0 = new Date();
          let date1 = new Date(value + ' ' + this.startTime);
          let milliseconds = date1 - date0;
          let hours = milliseconds / 1000 / 3600;
          if (hours < 48) {
            this.startTime = null;
          }
        }
        this.$store.commit('EVENT_MANAGER_STORE/UPDATE_EVENT_START_DATE_TIME', {
          key: 'selectedStartDate',
          value: value
        });

        if (!this.loading) {
          void this.loadData();
        }
      }
    },
  },

  data: () => ({
    endDateMenu: false,
    loading: false,
    startDateMenu: false,
  }),

  methods: {
    ...mapActions('PROXY_STORE', ['GET_PROXIES']),

    checkIfEndDateIsValid(endDate) {
      if (!endDate) return 'Event end date is required.';
      return true;
    },

    disablePastDates(val = '') {
      return val >= new Date().toISOString().substring(0, 10);
    },

    async loadData() {
      const evtId = this.$route.params.id;
      await this.$store.dispatch(
        'EVENT_MANAGER_STORE/LOAD_EVENT',
        evtId
      );
    },

    async fetchEventData() {
      this.loading = true;
      this.$store.commit('EVENT_MANAGER_STORE/RESET_ALL');
      await this.loadData();
      this.loading = false;
    },

    async downloadReportPDF() {
      if (this.$refs.generator) {
        await this.$refs.generator.createReportingPDF(this.CURRENT_EVENT_OBJECT);
      }
    },

    async downloadReportCSV() {
      ExportTools.exportReportingToCsv(this.CURRENT_EVENT_OBJECT);
    },
  },

  async mounted() {
    await this.fetchEventData();
  },
};
</script>
